import { useMutation } from 'react-query';
import { Optional } from 'ts-toolbelt/out/Object/Optional';
import { Options } from '@frontend/fetch';
import { LimitedSchemaMutationOptions } from '@frontend/react-query-helpers';
import { convertObjectToFieldMask, getObjectValueFromPath } from '@frontend/schema-field-mask';
import { Prettify } from '@frontend/types';
import { useMessagingSettingUpdater } from '../hooks/use-messaging-setting-updater';
import { SchemaMessagingSettingsV1Service } from '../service';
import { SetIO } from '../types';
import { convertPartialSettingToFullSetting, correctDependentSettingsInSetRequest } from '../utils';

export const getSetMutationKey = () => ['MessagingSettingsV1', 'Set'] as const;

type MutationContext<C = unknown> = {
  // Add internal mutation context here if needed (useful for optimistic updates)
  didOptimisticUpdate: boolean;
  revertFn?: () => void;
  otherContext?: C;
};

type SetIOOverride = Prettify<{
  output: SetIO['output'];
  input: Prettify<Optional<SetIO['input'], 'setting', 'deep'>>;
}>;
type UseSetMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends SetIOOverride['input'] = SetIOOverride['input']
> = {
  options?: LimitedSchemaMutationOptions<SetIOOverride, E, C, never, RequestOverride>;
  httpOptions?: Options;
  optimisticUpdate?: boolean;
};
/**
 * A hook that returns a mutation for the `Set` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * It also modifies the endpoint request type to allow for partial setting updates with auto-generated field masks.
 * If you provide a partial setting object, and don't provide a field mask, the field mask will be auto-generated based
 * on the provided object.
 *
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 *
 * Some settings are also dependent on each other, so if an invalid mixture of those settings are set, the mutation will
 * force the dependent settings to be set to a valid state. Here's a list of dependent settings:
 * ### Inbox Location Settings:
 * - `processConfirmationsQuietly` must be `true` if `processConfirmationsSilently` is `true`
 */
export const useSetMutation = <
  E = unknown,
  C = unknown,
  RequestOverride extends SetIOOverride['input'] = SetIOOverride['input']
>({ options, httpOptions, optimisticUpdate = false }: UseSetMutationArgs<E, C | undefined, RequestOverride> = {}) => {
  const settingsUpdater = useMessagingSettingUpdater();

  /**
   * Allows for TS partial objects to be used with an auto-generated field mask if the field mask isn't provided for
   * more intuitive partial setting updatedPaths
   */
  const getSetRequest = (req: SetIOOverride['input']): SetIO['input'] => {
    const requestWithCorrectDependents = correctDependentSettingsInSetRequest(req);
    const resolvedMask =
      requestWithCorrectDependents.mask ?? convertObjectToFieldMask(requestWithCorrectDependents.setting ?? {});
    const fullSetting = convertPartialSettingToFullSetting({
      settingType: requestWithCorrectDependents.settingType,
      setting: requestWithCorrectDependents.setting ?? {},
    });

    if (!fullSetting) throw new Error('Error resolving messaging setting request from partial');

    return {
      ...req,
      setting: fullSetting,
      mask: resolvedMask,
    };
  };

  return useMutation<SetIO['output'], E, RequestOverride, MutationContext<C>>({
    mutationKey: getSetMutationKey(),
    mutationFn: (req) => {
      return SchemaMessagingSettingsV1Service.Set(getSetRequest(req), httpOptions);
    },
    ...options,
    onMutate: async (request) => {
      // Implement optimistic updates here
      if (optimisticUpdate) {
        const revertFn = settingsUpdater(getSetRequest(request));
        return {
          otherContext: await options?.onMutate?.(request),
          didOptimisticUpdate: true,
          revertFn,
        };
      }

      return {
        // Add custom context type here (if exists in `MutationContext`)
        didOptimisticUpdate: false,
        otherContext: await options?.onMutate?.(request),
      };
    },
    onSuccess: (response, request, context) => {
      // Implement query updates or invalidation for affected queries here
      const updatedPaths = request.mask?.paths ?? convertObjectToFieldMask(request.setting ?? {})?.paths ?? [];
      const updatedValuesDontMatch = updatedPaths.some((path) => {
        const requestValue = getObjectValueFromPath(request.setting ?? {}, path);
        const responseValue = getObjectValueFromPath(response.setting, path);
        return requestValue !== responseValue;
      });
      const needsUpdate = context?.didOptimisticUpdate ? updatedValuesDontMatch : true;
      if (needsUpdate) {
        settingsUpdater(response);
      }
      return options?.onSuccess?.(response, request, context?.otherContext);
    },
    onError: (error, request, context) => {
      // Revert optimistic update here (if implemented)
      if (context?.didOptimisticUpdate) {
        context?.revertFn?.();
      }
      return options?.onError?.(error, request, context?.otherContext);
    },
    onSettled: (response, error, request, context) => {
      // Only pass context of type C into provided `onSettled` option
      return options?.onSettled?.(response, error, request, context?.otherContext);
    },
  });
};
